<template>
  <div>
    <VanField
      v-model="score.name"
      :label="$t('Chats.chatScene.edit.name')"
      :placeholder="$t('Chats.chatScene.edit.numericalNamePlaceholder')"
      :rules="[
        {
          required: true,
          message: $t('Chats.chatScene.edit.numericalNamePlaceholder'),
        },
      ]"
      required
      @blur="_updateChatSceneScore"
    >
      <template #label>
        <span>
          {{ $t("Chats.chatScene.edit.name") }}
        </span>
        <i
          @click="clickNameInfo"
          class="iconfont icon-info text-xs ml-1 cursor-pointer"
        />
      </template>
      <template #right-icon>
        <i
          v-if="score.id"
          class="iconfont icon-delete text-xs translate-y-1 ml-1 cursor-pointer"
          @click="clickDelete"
        />
      </template>
    </VanField>
    <VanField
      :model-value="score.defaultScore"
      is-link
      readonly
      :label="$t('Chats.chatScene.edit.default')"
      required
      @click="clickOpenScoreSliderModal"
    />
    <VanField
      :model-value="$t(`Chats.chatScene.edit.scoreType${score.type}`)"
      is-link
      readonly
      :label="$t('Chats.chatScene.edit.type')"
      required
      @click="showScoreTypeSelectPopup = true"
    />
    <div class="relative flex bg-base-200 px-3 pt-2 pb-4">
      <div class="form-control w-full">
        <div class="flex w-full items-center justify-between">
          <div class="label pt-0">
            <div class="flex items-center label-text required-field">
              {{ $t("Chats.chatScene.edit.shortDefinition") }}
            </div>
          </div>
        </div>
        <textarea
          ref="textarea"
          v-model="score.scoringInstruction"
          :placeholder="$t('Chats.chatScene.edit.shortDefinitionPlaceholder')"
          class="input input-bordered w-full rounded textarea min-h-[3rem] text-xs join-item text-base-content overflow-hidden"
          @blur="_updateChatSceneScore"
        />
      </div>
    </div>
    <div class="flex flex-col gap-2 py-2 bg-base-200">
      <div class="flex pl-4">
        <span class="text-[#ee0a24] mr-[2px]">*</span>
        <span>
          {{ $t("Chats.chatScene.edit.reaction") }}
        </span>
      </div>
      <div class="flex flex-col gap-2 pl-6 pr-4">
        <div
          v-for="(d, idx) in score.definitionzz"
          :key="idx"
          class="flex gap-2 justify-between items-center w-full overflow-hidden"
        >
          <div class="break-words flex-1">
            {{ d.from === null ? "-∞" : d.from }} ~
            {{ d.to === null ? "+∞" : d.to }} : {{ d.definition }}
          </div>
          <div>
            <button
              class="btn btn-xs btn-neutral text-base-content/50 gap-0"
              @click="clickEditReaction({ ...d, idx })"
            >
              <i class="iconify mdi--pencil-outline text-xs" />
            </button>
          </div>
        </div>
      </div>
      <div class="flex justify-end pr-4">
        <button
          class="btn btn-sm btn-neutral text-base-content/50 gap-0"
          @click="clickEditReaction()"
        >
          <i class="iconify mdi--plus text-xs" />
          {{ $t("Buttons.add") + " " + $t("Chats.chatScene.edit.reaction") }}
        </button>
      </div>
    </div>
    <div class="px-4 pb-2 mb-4 bg-base-200">
      <div class="flex pl-2.5 mb-1 border-t border-base-content/10 pt-2">
        {{ $t("Chats.chatScene.edit.applyCharacter") }}
      </div>

      <div class="flex flex-wrap gap-2 my-2">
        <div
          class="flex flex-col items-center gap-0.5 relative"
          v-for="character in score.characterzz"
          :key="character.id"
        >
          <i
            class="iconfont icon-close absolute top-[-8px] right-[-8px] z-10 text-xs bg-base-100 rounded-full cursor-pointer"
            @click="removeCharacter(character.id!)"
          />
          <UserAvatar
            class="w-16"
            :avatar-url="mateStore.mateIdMap.get(character.mateId)?.mediaUrl"
          />
          <div class="text-xs max-w-16 line-clamp-1">
            {{
              character.role === 2
                ? $t("game.player")
                : mateStore.mateIdMap.get(character.mateId)?.name
            }}
          </div>

          <div class="text-xs">
            {{ $t("Chats.chatScene.edit.default") }}:
            {{ character.defaultScore }}
            <i
              class="iconify mingcute--edit-3-line text-xs p-0.5"
              @click="clickEditDefaultScore(character)"
            />
          </div>
        </div>
      </div>

      <div class="flex justify-end pr-4">
        <button
          class="btn btn-sm btn-neutral text-base-content/50 gap-0"
          @click="clickAddCharacter()"
        >
          <i class="iconify mdi--plus text-xs" />
          {{ $t("Buttons.add") + " " + $t("Chats.chatScene.edit.character") }}
        </button>
      </div>
    </div>
  </div>

  <BaseScoreSliderModal
    v-model:show="showScoreSliderModal"
    :title="showScoreTitle"
    :score="showScore"
    @confirm="confirmScore"
  />

  <ReactionEdit
    v-model:show="showReactionEdit"
    :definition="definition"
    :mate-context="mateContext"
    :name="score.name"
    @update:definition="onSaveReactionDefinition"
    :chat-scene="chatScene"
  />

  <ModalSelectCharacter
    v-model:show="showCharacterSelectPopup"
    :selected-ids="[...props.score.characterzz?.map((c) => c.id!)]"
    :character-list="props.chatScene.characterzz"
    @select="onSelectCharacter"
  />

  <ModalSelectOption
    v-model:show="showScoreTypeSelectPopup"
    :options="[
      { text: $t(`Chats.chatScene.edit.scoreType1`), value: 1 },
      { text: $t(`Chats.chatScene.edit.scoreType2`), value: 2 },
    ]"
    @confirm="onSelectScoreType"
  />
</template>

<script setup lang="ts">
import {
  createChatSceneScore,
  createChatSceneScoreDefinition,
  deleteChatSceneScore,
  deleteChatSceneScoreDefinition,
  updateChatSceneScore,
  updateChatSceneScoreDefinition,
} from "@/apis/chatScene";
import { useChatScene } from "@/stores/chatScene";
import { useMate } from "@/stores/mate";
import {
  ChatScene,
  ChatSceneCharacter,
  ChatSceneScore,
  ChatSceneScoreDefinition,
  GenerateMateContext,
} from "@/types/chat";
import { showConfirmDialog, showDialog } from "vant";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const chatSceneStore = useChatScene();
const props = defineProps<{
  chatScene: ChatScene;
  score: ChatSceneScore;
  idx: number;
  mateContext: GenerateMateContext;
}>();

const mateStore = useMate();
const clickNameInfo = () => {
  showDialog({
    message: t("Chats.chatScene.edit.numericalNameInfo"),
    confirmButtonText: t("Buttons.confirm"),
  });
};

const showScoreSliderModal = ref(false);
const showScoreType = ref<"default" | "character">("default");
const showScore = ref(0);
const showScoreTitle = ref("");
const clickOpenScoreSliderModal = () => {
  showScoreType.value = "default";
  showScoreSliderModal.value = true;
  showScore.value = props.score.defaultScore;
  showScoreTitle.value = t("Chats.chatScene.edit.default");
};

const confirmScore = async (score: number) => {
  showScoreSliderModal.value = false;
  if (showScoreType.value === "default") {
    props.score.defaultScore = score;
  } else if (showScoreType.value === "character") {
    const character = props.score.characterzz?.find(
      (c) => c.id === activeCharacterIdx.value
    );
    if (character) character.defaultScore = score;
  }
  await _updateChatSceneScore();
};

const _updateChatSceneScore = async () => {
  chatSceneStore.withSaving(async () => {
    if (!props.score.id) {
      if (!props.score.name) return;
      const res = await createChatSceneScore({
        chatSceneId: props.chatScene.id!,
        name: props.score.name,
        defaultScore: props.score.defaultScore,
        type: props.score.type,
      });
      props.score.id = res.item.id;
    } else {
      await updateChatSceneScore(props.score.id!, props.score);
    }
  });
};

const showReactionEdit = ref(false);
const definition = ref<ChatSceneScoreDefinition>();
const clickEditReaction = async (_definition?: ChatSceneScoreDefinition) => {
  if (!_definition) {
    // 新增
    if (!props.score.definitionzz) props.score.definitionzz = [];

    if (!props.score.name)
      return showDialog({
        message: t("Message.pleaseInputNumericalName"),
        confirmButtonText: t("Buttons.confirm"),
        showCancelButton: false,
      });
  }
  definition.value =
    _definition ||
    ({
      scoreId: props.score.id,
      from: 0,
      to: 0,
      definition: "",
      idx: -1,
    } as ChatSceneScoreDefinition);
  showReactionEdit.value = true;
};

const onSaveReactionDefinition = (d: ChatSceneScoreDefinition) => {
  const { idx, ...rest } = d;
  chatSceneStore.withSaving(async () => {
    if (idx === -1) {
      const res = await createChatSceneScoreDefinition({
        ...rest,
        scoreId: props.score.id!,
      });
      props.score.definitionzz!.push(res.item);
    } else if (
      rest.from === -9999 &&
      rest.to === -9999 &&
      rest.definition === "removed reaction"
    ) {
      await deleteChatSceneScoreDefinition(rest.id!);
      props.score.definitionzz!.splice(idx!, 1);
    } else {
      const res = await updateChatSceneScoreDefinition(rest.id!, rest);
      props.score.definitionzz![idx!] = res.item;
    }
  });
};

const showCharacterSelectPopup = ref(false);
const clickAddCharacter = () => {
  if (!props.score.name)
    return showDialog({
      message: t("Message.pleaseInputNumericalName"),
      confirmButtonText: t("Buttons.confirm"),
      showCancelButton: false,
    });
  showCharacterSelectPopup.value = true;
};

const onSelectCharacter = async (character: ChatSceneCharacter) => {
  props.score.characterzz!.push({
    ...character,
    defaultScore: props.score.defaultScore,
  });
  await _updateChatSceneScore();
  showCharacterSelectPopup.value = false;
};

const removeCharacter = async (characterId: number) => {
  props.score.characterzz = props.score.characterzz?.filter(
    (c) => c.id !== characterId
  );
  await _updateChatSceneScore();
};

const activeCharacterIdx = ref(0);
const clickEditDefaultScore = (character: ChatSceneCharacter) => {
  activeCharacterIdx.value = character.id!;
  showScoreSliderModal.value = true;
  showScoreType.value = "character";
  showScore.value = character.defaultScore ?? 0;
  showScoreTitle.value = t("Chats.chatScene.edit.default");
};

const clickDelete = () => {
  showConfirmDialog({
    title: t("Chats.chatScene.edit.deleteScore"),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(async () => {
    props.chatScene.scorezz = props.chatScene.scorezz!.filter(
      (s) => s.id !== props.score.id
    );
    chatSceneStore.withSaving(async () => {
      await deleteChatSceneScore(props.score.id!);
    });
  });
};

const showScoreTypeSelectPopup = ref(false);
const onSelectScoreType = (type: number) => {
  props.score.type = type;
  showScoreTypeSelectPopup.value = false;
  _updateChatSceneScore();
};
</script>
